import {
  ChangeEventHandler,
  FocusEventHandler,
  HTMLInputTypeAttribute,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Loader } from './Loader';
import classNames from 'classnames';
import { Button, ButtonComponentPropsType } from './Button';

interface Props {
  label?: string | undefined;
  helperText?: ReactNode | undefined;
  value: number | string;
  className?: string | undefined;
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  type?: HTMLInputTypeAttribute | 'textarea' | undefined;
  required?: boolean | undefined;
  Icon?: HeroIconType | undefined;
  IconEnd?: HeroIconType | undefined;
  error?: string | undefined;
  success?: string | undefined;
  max?: number | undefined;
  min?: number | undefined;
  inputClassname?: string | undefined;
  pattern?: string | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  inlineButton?: ButtonComponentPropsType | undefined;
  inverted?: boolean | undefined;
}

export const NumberInput = ({
  onChange,
  ...rest
}: Exclude<Props, 'pattern'>) => (
  <Input
    onChange={(e) => {
      const matches = e.target.value.match(/^[0-9]*[\\.]{0,1}[0-9]{0,2}$/);
      if (!!matches || e.target.value.length === 0) {
        onChange(e);
      }
    }}
    pattern="^[0-9]*[\.]{0,1}[0-9]{0,2}$"
    {...rest}
  />
);

export const Input = ({
  value,
  label,
  helperText,
  onChange,
  className = '',
  type = 'text',
  required = false,
  Icon,
  error,
  success,
  max,
  min,
  onBlur,
  inputClassname = '',
  placeholder,
  pattern,
  loading,
  disabled,
  IconEnd,
  inlineButton,
  inverted
}: Props): ReactElement => {
  const [inputType, setInputType] = useState(type);

  return (
    <div className={`flex flex-col ${className}`}>
      {!!label && (
        <label
          htmlFor={label}
          className={`${helperText ? '' : 'mb-2'} font-semibold text-body-medium`}
        >
          {label}
        </label>
      )}
      {!!helperText && (
        <span className="mb-2 mt-1 text-text-low-priority text-sm">{helperText}</span>
      )}
      <div
        className={classNames(
          'group -outline-offset-1 flex flex-col border relative rounded-xs overflow-hidden transition-all duration-100 focus-within:outline-2',
          inverted
            ? 'border-black-t-700 focus-within:outline-black bg-white'
            : 'border-grey-t-700 focus-within:outline-white bg-background-secondary',
          {
            'border-red-300': error?.length,
            'text-text-disabled': disabled,
            'hover:border-white': !disabled && !inverted,
            'hover:border-black': !disabled && inverted,
          }
        )}
      >
        <div className="relative flex">
          {Icon ? (
            <div className="absolute top-1/2 transform -translate-y-1/2 ml-3">
              <Icon
                className={classNames('size-5', {
                  'stroke-text-disabled': disabled,
                })}
              />
            </div>
          ) : (
            <></>
          )}
          {type !== 'textarea' ? (
            <input
              className={classNames(
                `w-full rounded outline-none disabled:text-text-disabled py-3 leading-none px-3 h-11 ${inputClassname}`,
                {
                  'pl-12': !!Icon,
                  'pr-12': !!IconEnd,
                }
              )}
              value={value}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
              type={inputType}
              required={required}
              max={max}
              min={min}
              id={label}
              name={label}
              placeholder={placeholder}
            />
          ) : (
            <textarea
              className={`w-full rounded outline-none disabled:text-text-disabled p-3 ${inputClassname}`}
              value={value}
              disabled={disabled}
              onChange={onChange}
              rows={4}
              required={required}
              onBlur={onBlur}
              id={label}
              maxLength={max}
              name={label}
            />
          )}
          {type === 'password' && (
            <button
              type="button"
              onPointerDown={(e) => e.preventDefault()}
              onClick={() =>
                inputType === 'password'
                  ? setInputType('text')
                  : setInputType('password')
              }
              className="absolute text-primary right-4 top-1/2 -translate-y-1/2"
            >
              {inputType === 'password' ? (
                <EyeIcon className="size-5" />
              ) : (
                <EyeSlashIcon className="size-5" />
              )}
            </button>
          )}

          {inlineButton && <Button {...inlineButton} />}

          {loading && (
            <Loader className="size-5 absolute top-1/2 right-4 -translate-y-1/2" />
          )}
          {IconEnd ? (
            <div className="absolute top-1/2 transform right-0 -translate-y-1/2 mr-3">
              <IconEnd
                className={classNames('size-5', {
                  'stroke-text-disabled': disabled,
                })}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
      {error && (
        <div className="flex space-x-1.5 items-start mt-2">
          <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-[3px]" />
          <span className="text-body-smallest text-red-300">{error}</span>
        </div>
      )}
      {type === 'textarea' && max && (
        <div className="flex mt-2 items-center justify-end">
          <span className="text-low-priority">
            {(value ? String(value) : '').length}/{max} characters
          </span>
        </div>
      )}
    </div>
  );
};
